import React, { useState, useEffect } from 'react';
import { Sun, Moon, MoonStar, Trees, Sunset as SunsetIcon, Flower2, Monitor, Check, Eye, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

export interface ThemeConfig {
  name: string;
  value: string;
  tagline: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  isDark: boolean;
  colors: {
    background: string;
    card: string;
    cardBorder: string;
    primary: string;
    primaryForeground: string;
    foreground: string;
    mutedForeground: string;
    secondary: string;
    accent: string;
    success: string;
    badgeBg: string;
    badgeText: string;
  };
}

export const THEME_DEFINITIONS: ThemeConfig[] = [
  {
    name: 'Light',
    value: 'light',
    tagline: 'Clean & Emerald',
    description: 'Crisp light slate surfaces with refined emerald green accents and warm amber highlights.',
    icon: Sun,
    isDark: false,
    colors: {
      background: '#f8fafc',
      card: '#ffffff',
      cardBorder: '#e2e8f0',
      primary: '#10b981',
      primaryForeground: '#ffffff',
      foreground: '#0f172a',
      mutedForeground: '#64748b',
      secondary: '#f1f5f9',
      accent: '#f59e0b',
      success: '#10b981',
      badgeBg: '#dcfce7',
      badgeText: '#15803d',
    },
  },
  {
    name: 'Dark',
    value: 'dark',
    tagline: 'Cyber Cyan',
    description: 'Deep obsidian surfaces paired with electric cyan highlights and high-contrast typography.',
    icon: Moon,
    isDark: true,
    colors: {
      background: '#0a0a0a',
      card: '#121212',
      cardBorder: '#262626',
      primary: '#06b6d4',
      primaryForeground: '#000000',
      foreground: '#f5f5f5',
      mutedForeground: '#a3a3a3',
      secondary: '#1c1c1c',
      accent: '#22d3ee',
      success: '#10b981',
      badgeBg: '#164e63',
      badgeText: '#67e8f9',
    },
  },
  {
    name: 'Midnight',
    value: 'midnight',
    tagline: 'Royal Navy',
    description: 'Deep oceanic navy canvas infused with vivid royal cobalt blue and crisp silver accents.',
    icon: MoonStar,
    isDark: true,
    colors: {
      background: '#050c18',
      card: '#0a1628',
      cardBorder: '#162b4e',
      primary: '#3b82f6',
      primaryForeground: '#ffffff',
      foreground: '#e2e8f0',
      mutedForeground: '#94a3b8',
      secondary: '#0f2342',
      accent: '#60a5fa',
      success: '#22c55e',
      badgeBg: '#1e3a8a',
      badgeText: '#93c5fd',
    },
  },
  {
    name: 'Forest',
    value: 'forest',
    tagline: 'Pine & Emerald',
    description: 'Earthy pine forest night with glowing emerald greens, perfect for poker table aesthetics.',
    icon: Trees,
    isDark: true,
    colors: {
      background: '#040d07',
      card: '#081a0e',
      cardBorder: '#12381e',
      primary: '#22c55e',
      primaryForeground: '#ffffff',
      foreground: '#f0fdf4',
      mutedForeground: '#86efac',
      secondary: '#0e2b18',
      accent: '#4ade80',
      success: '#22c55e',
      badgeBg: '#14532d',
      badgeText: '#86efac',
    },
  },
  {
    name: 'Sunset',
    value: 'sunset',
    tagline: 'Plum & Amber',
    description: 'Dramatic violet-plum dark canvas with glowing solar orange and amber sunset gradients.',
    icon: SunsetIcon,
    isDark: true,
    colors: {
      background: '#0d0414',
      card: '#180a24',
      cardBorder: '#35164f',
      primary: '#f97316',
      primaryForeground: '#ffffff',
      foreground: '#fdf4ff',
      mutedForeground: '#d8b4fe',
      secondary: '#28113c',
      accent: '#fb923c',
      success: '#22c55e',
      badgeBg: '#581c87',
      badgeText: '#fde047',
    },
  },
  {
    name: 'Rose',
    value: 'rose',
    tagline: 'Crimson Velvet',
    description: 'Rich ruby crimson base with vibrant neon rose accents for a bold, distinctive style.',
    icon: Flower2,
    isDark: true,
    colors: {
      background: '#120409',
      card: '#200812',
      cardBorder: '#441227',
      primary: '#f43f5e',
      primaryForeground: '#ffffff',
      foreground: '#fff1f2',
      mutedForeground: '#fda4af',
      secondary: '#360c1d',
      accent: '#fb7185',
      success: '#22c55e',
      badgeBg: '#881337',
      badgeText: '#fecdd3',
    },
  },
  {
    name: 'System',
    value: 'system',
    tagline: 'Auto Adapt',
    description: 'Automatically synchronizes with your device or operating system light & dark mode preference.',
    icon: Monitor,
    isDark: false,
    colors: {
      background: '#1e293b',
      card: '#334155',
      cardBorder: '#475569',
      primary: '#60a5fa',
      primaryForeground: '#ffffff',
      foreground: '#f8fafc',
      mutedForeground: '#94a3b8',
      secondary: '#475569',
      accent: '#38bdf8',
      success: '#34d399',
      badgeBg: '#1e293b',
      badgeText: '#93c5fd',
    },
  },
];

interface ThemePreviewSectionProps {
  currentTheme: string | undefined;
  onSelectTheme: (themeValue: string) => void;
}

export function ThemePreviewSection({ currentTheme, onSelectTheme }: ThemePreviewSectionProps) {
  const { toast } = useToast();
  const [stagedTheme, setStagedTheme] = useState<string>(currentTheme || 'light');

  // Sync staged theme when currentTheme changes externally
  useEffect(() => {
    if (currentTheme) {
      setStagedTheme(currentTheme);
    }
  }, [currentTheme]);

  const activeThemeConfig = THEME_DEFINITIONS.find((t) => t.value === currentTheme) || THEME_DEFINITIONS[0];
  const stagedThemeConfig = THEME_DEFINITIONS.find((t) => t.value === stagedTheme) || activeThemeConfig;

  const handleApplyTheme = (themeValue: string) => {
    onSelectTheme(themeValue);
    setStagedTheme(themeValue);
    const target = THEME_DEFINITIONS.find((t) => t.value === themeValue);
    toast({
      title: `Applied ${target?.name || themeValue} Theme`,
      description: target?.description || 'Theme preference updated.',
    });
  };

  const isStagedApplied = stagedTheme === currentTheme;

  return (
    <div className="space-y-4">
      {/* Header with Title and Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Color Themes & Live Preview</p>
            <p className="text-xs text-muted-foreground">Select or preview color themes before applying</p>
          </div>
        </div>
        <Badge variant="outline" className="text-xs capitalize font-medium flex items-center gap-1.5 py-1 px-2.5">
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          Active: <strong className="font-semibold text-foreground">{activeThemeConfig.name}</strong>
        </Badge>
      </div>

      {/* Interactive Visual Live Preview Stage */}
      <div
        id="theme-live-preview-stage"
        className="rounded-2xl border transition-all duration-300 p-4 shadow-sm overflow-hidden relative"
        style={{
          backgroundColor: stagedThemeConfig.colors.background,
          borderColor: stagedThemeConfig.colors.cardBorder,
          color: stagedThemeConfig.colors.foreground,
        }}
      >
        {/* Preview Stage Header */}
        <div className="flex items-center justify-between pb-3 border-b mb-3.5" style={{ borderColor: stagedThemeConfig.colors.cardBorder }}>
          <div className="flex items-center gap-2.5">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center shadow-xs"
              style={{
                backgroundColor: stagedThemeConfig.colors.primary,
                color: stagedThemeConfig.colors.primaryForeground,
              }}
            >
              <stagedThemeConfig.icon className="h-4 w-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold tracking-tight" style={{ color: stagedThemeConfig.colors.foreground }}>
                  {stagedThemeConfig.name} Theme Preview
                </span>
                <span
                  className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                  style={{
                    backgroundColor: stagedThemeConfig.colors.badgeBg,
                    color: stagedThemeConfig.colors.badgeText,
                  }}
                >
                  {stagedThemeConfig.tagline}
                </span>
              </div>
              <p className="text-[11px] line-clamp-1 opacity-80" style={{ color: stagedThemeConfig.colors.mutedForeground }}>
                {stagedThemeConfig.description}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isStagedApplied ? (
              <Button
                size="sm"
                className="h-7 text-xs font-semibold px-3 shadow-sm transition-transform active:scale-95"
                style={{
                  backgroundColor: stagedThemeConfig.colors.primary,
                  color: stagedThemeConfig.colors.primaryForeground,
                }}
                onClick={() => handleApplyTheme(stagedTheme)}
              >
                <Check className="h-3.5 w-3.5 mr-1" />
                Apply {stagedThemeConfig.name}
              </Button>
            ) : (
              <span
                className="text-[11px] font-semibold flex items-center gap-1 px-2.5 py-1 rounded-full"
                style={{
                  backgroundColor: stagedThemeConfig.colors.secondary,
                  color: stagedThemeConfig.colors.foreground,
                  border: `1px solid ${stagedThemeConfig.colors.cardBorder}`,
                }}
              >
                <Check className="h-3 w-3 text-emerald-500" />
                Active Theme
              </span>
            )}
          </div>
        </div>

        {/* Realistic Interactive Mini App Mockup */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Card 1: Sample Session Card */}
          <div
            className="rounded-xl p-3 border transition-all shadow-xs space-y-2.5"
            style={{
              backgroundColor: stagedThemeConfig.colors.card,
              borderColor: stagedThemeConfig.colors.cardBorder,
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
                  style={{
                    backgroundColor: stagedThemeConfig.colors.primary,
                    color: stagedThemeConfig.colors.primaryForeground,
                  }}
                >
                  ♠
                </div>
                <div>
                  <p className="text-xs font-bold leading-tight" style={{ color: stagedThemeConfig.colors.foreground }}>
                    Saturday Night High Stakes
                  </p>
                  <p className="text-[10px]" style={{ color: stagedThemeConfig.colors.mutedForeground }}>
                    6 Players · ₹200 Buy-in
                  </p>
                </div>
              </div>
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: stagedThemeConfig.colors.badgeBg,
                  color: stagedThemeConfig.colors.badgeText,
                }}
              >
                COMPLETED
              </span>
            </div>

            <div
              className="grid grid-cols-3 gap-1.5 p-2 rounded-lg text-center"
              style={{
                backgroundColor: stagedThemeConfig.colors.secondary,
              }}
            >
              <div>
                <p className="text-[9px] uppercase tracking-wider font-medium" style={{ color: stagedThemeConfig.colors.mutedForeground }}>
                  Pot Total
                </p>
                <p className="text-xs font-bold" style={{ color: stagedThemeConfig.colors.foreground }}>
                  ₹12,000
                </p>
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-wider font-medium" style={{ color: stagedThemeConfig.colors.mutedForeground }}>
                  Top Winner
                </p>
                <p className="text-xs font-bold" style={{ color: stagedThemeConfig.colors.primary }}>
                  +₹4,200
                </p>
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-wider font-medium" style={{ color: stagedThemeConfig.colors.mutedForeground }}>
                  Duration
                </p>
                <p className="text-xs font-bold" style={{ color: stagedThemeConfig.colors.foreground }}>
                  3h 45m
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <span className="text-[11px] font-medium" style={{ color: stagedThemeConfig.colors.mutedForeground }}>
                MVP: <strong style={{ color: stagedThemeConfig.colors.foreground }}>Karnakar</strong>
              </span>
              <div
                className="text-[10px] font-semibold px-2 py-1 rounded-md flex items-center gap-1 cursor-pointer transition-opacity hover:opacity-80"
                style={{
                  backgroundColor: stagedThemeConfig.colors.primary,
                  color: stagedThemeConfig.colors.primaryForeground,
                }}
                onClick={() => handleApplyTheme(stagedTheme)}
              >
                View Session
                <ArrowRight className="h-2.5 w-2.5" />
              </div>
            </div>
          </div>

          {/* Card 2: Sample Leaderboard / Player Rank */}
          <div
            className="rounded-xl p-3 border transition-all shadow-xs space-y-2.5 flex flex-col justify-between"
            style={{
              backgroundColor: stagedThemeConfig.colors.card,
              borderColor: stagedThemeConfig.colors.cardBorder,
            }}
          >
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold" style={{ color: stagedThemeConfig.colors.foreground }}>
                  Group Standings Preview
                </span>
                <span className="text-[10px] font-medium" style={{ color: stagedThemeConfig.colors.mutedForeground }}>
                  Season 2026
                </span>
              </div>

              {/* Mini row 1 */}
              <div
                className="flex items-center justify-between p-1.5 rounded-lg mb-1.5 text-xs"
                style={{ backgroundColor: stagedThemeConfig.colors.secondary }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold w-4 text-center" style={{ color: stagedThemeConfig.colors.primary }}>
                    #1
                  </span>
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold"
                    style={{
                      backgroundColor: stagedThemeConfig.colors.primary,
                      color: stagedThemeConfig.colors.primaryForeground,
                    }}
                  >
                    KP
                  </div>
                  <span className="font-semibold text-xs" style={{ color: stagedThemeConfig.colors.foreground }}>
                    Karnakar
                  </span>
                </div>
                <span className="font-bold text-xs" style={{ color: stagedThemeConfig.colors.primary }}>
                  +₹18,400
                </span>
              </div>

              {/* Mini row 2 */}
              <div className="flex items-center justify-between p-1.5 rounded-lg text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold w-4 text-center opacity-60" style={{ color: stagedThemeConfig.colors.mutedForeground }}>
                    #2
                  </span>
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold opacity-80"
                    style={{
                      backgroundColor: stagedThemeConfig.colors.mutedForeground,
                      color: stagedThemeConfig.colors.background,
                    }}
                  >
                    SS
                  </div>
                  <span className="font-semibold text-xs opacity-90" style={{ color: stagedThemeConfig.colors.foreground }}>
                    Suresh
                  </span>
                </div>
                <span className="font-bold text-xs" style={{ color: stagedThemeConfig.colors.accent }}>
                  +₹7,150
                </span>
              </div>
            </div>

            {/* Color Palette Breakdown Bar */}
            <div className="pt-2 border-t flex items-center justify-between" style={{ borderColor: stagedThemeConfig.colors.cardBorder }}>
              <span className="text-[10px] uppercase font-bold tracking-wider" style={{ color: stagedThemeConfig.colors.mutedForeground }}>
                Palette Swatches
              </span>
              <div className="flex items-center gap-1.5">
                {[
                  { label: 'Surface', color: stagedThemeConfig.colors.background },
                  { label: 'Card', color: stagedThemeConfig.colors.card },
                  { label: 'Primary', color: stagedThemeConfig.colors.primary },
                  { label: 'Accent', color: stagedThemeConfig.colors.accent },
                  { label: 'Badge', color: stagedThemeConfig.colors.badgeText },
                ].map((swatch, idx) => (
                  <div
                    key={idx}
                    title={`${swatch.label}: ${swatch.color}`}
                    className="w-4 h-4 rounded-full border shadow-xs transition-transform hover:scale-125"
                    style={{
                      backgroundColor: swatch.color,
                      borderColor: stagedThemeConfig.colors.cardBorder,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid of Theme Selection Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {THEME_DEFINITIONS.map((t) => {
          const isActive = currentTheme === t.value;
          const isStaged = stagedTheme === t.value;
          const Icon = t.icon;

          return (
            <div
              key={t.value}
              className={cn(
                "rounded-2xl border-2 transition-all p-3.5 flex flex-col justify-between gap-3 relative cursor-pointer group",
                isActive
                  ? "border-primary bg-primary/5 ring-2 ring-primary/20 shadow-sm"
                  : isStaged
                  ? "border-muted-foreground/50 bg-accent/5 shadow-xs"
                  : "border-border hover:border-muted-foreground/40 bg-card"
              )}
              onClick={() => {
                setStagedTheme(t.value);
                onSelectTheme(t.value);
              }}
            >
              {/* Top Row: Theme Name, Icon, Tagline and Status Badge */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shadow-xs transition-transform group-hover:scale-105"
                    style={{
                      backgroundColor: t.colors.card,
                      border: `1px solid ${t.colors.cardBorder}`,
                      color: t.colors.primary,
                    }}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    {/* Button element with the exact theme name for accessibility & test selectors */}
                    <button
                      type="button"
                      aria-label={t.name}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApplyTheme(t.value);
                      }}
                      className={cn(
                        "text-sm font-bold block text-left hover:underline focus:outline-none",
                        isActive ? "text-primary" : "text-foreground"
                      )}
                    >
                      {t.name}
                    </button>
                    <p className="text-[11px] text-muted-foreground font-medium">{t.tagline}</p>
                  </div>
                </div>

                {isActive && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary text-primary-foreground shadow-xs">
                    <Check className="h-3 w-3" />
                    Active
                  </span>
                )}
              </div>

              {/* Miniature UI Mockup Frame */}
              <div
                className="rounded-xl p-2.5 border transition-all text-[10px] space-y-1.5 shadow-inner"
                style={{
                  backgroundColor: t.colors.background,
                  borderColor: t.colors.cardBorder,
                  color: t.colors.foreground,
                }}
              >
                {/* Mini mockup navbar */}
                <div className="flex items-center justify-between pb-1 border-b" style={{ borderColor: t.colors.cardBorder }}>
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.colors.primary }} />
                    <span className="font-semibold text-[9px]" style={{ color: t.colors.foreground }}>
                      SSC Poker
                    </span>
                  </div>
                  <div
                    className="w-10 h-1.5 rounded-full"
                    style={{ backgroundColor: t.colors.secondary }}
                  />
                </div>

                {/* Mini mockup card */}
                <div
                  className="rounded-lg p-1.5 border flex items-center justify-between"
                  style={{
                    backgroundColor: t.colors.card,
                    borderColor: t.colors.cardBorder,
                  }}
                >
                  <div className="space-y-0.5">
                    <div className="w-12 h-1.5 rounded-full" style={{ backgroundColor: t.colors.foreground, opacity: 0.8 }} />
                    <div className="w-8 h-1 rounded-full" style={{ backgroundColor: t.colors.mutedForeground, opacity: 0.5 }} />
                  </div>
                  <div
                    className="px-1.5 py-0.5 rounded text-[8px] font-bold"
                    style={{
                      backgroundColor: t.colors.primary,
                      color: t.colors.primaryForeground,
                    }}
                  >
                    +₹2.4k
                  </div>
                </div>
              </div>

              {/* Bottom Action & Swatch Bar */}
              <div className="flex items-center justify-between pt-1 border-t border-border/50">
                {/* Swatches */}
                <div className="flex items-center gap-1">
                  {[t.colors.background, t.colors.card, t.colors.primary, t.colors.accent].map((c, i) => (
                    <div
                      key={i}
                      className="w-3.5 h-3.5 rounded-full border shadow-xs"
                      style={{ backgroundColor: c, borderColor: t.colors.cardBorder }}
                    />
                  ))}
                </div>

                {/* Quick Preview or Apply Button */}
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setStagedTheme(t.value);
                    }}
                    className={cn(
                      "text-[11px] font-medium px-2 py-0.5 rounded-md flex items-center gap-1 transition-colors",
                      isStaged ? "text-primary font-semibold bg-primary/10" : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <Eye className="h-3 w-3" />
                    Preview
                  </button>

                  <Button
                    size="sm"
                    variant={isActive ? "secondary" : "default"}
                    className={cn(
                      "h-6 text-[11px] px-2.5 font-semibold",
                      isActive ? "opacity-75 cursor-default" : ""
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApplyTheme(t.value);
                    }}
                  >
                    {isActive ? "Active" : "Apply"}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
